// Fehler der Apps und des Servers für GlitchTip (PLAN.md, Abschnitt 3,
// „Fehler und Alarm“; O3). GlitchTip versteht das Format von Sentry; hier
// entsteht daraus ein Ereignis – bewusst schmal: Art, Meldung, Stelle im
// Code, App, Umgebung. Keine Eingaben, keine Adressen mit Kennungen, keine
// Cookies, keine Kundendaten (CLAUDE.md, Datenschutz: Alarme enthalten nie
// Kundendaten). Vom Stapel nur die Stellen im Code (nurStellenImCode, seit
// dem Gutachten vom 3.11.2026, Befund 4); was ein Fehler der Datenbank am
// Server sagen darf, regelt server/src/fehler-ohne-daten.ts.

import * as z from "zod";

export const fehlerMeldungEingabe = z.object({
  app: z.enum(["doku", "crm"]),
  typ: z.string().max(200),
  meldung: z.string().max(2000),
  stapel: z.string().max(20_000).optional(),
  /** Die Seite der App (#/proben) – ohne Kennungen, ohne Suchteil. */
  ort: z.string().max(200).optional(),
});
export type FehlerMeldung = z.infer<typeof fehlerMeldungEingabe> | (Omit<z.infer<typeof fehlerMeldungEingabe>, "app"> & { app: "server" });

export interface StapelZeile {
  function?: string;
  filename?: string;
  lineno?: number;
  colno?: number;
}

/**
 * Liest die Stellen im Code aus einem Stapel – in beiden Schreibweisen:
 * Chrome und Node („at name (datei:1:2)“) und Safari („name@datei:1:2“).
 */
export function stapelZeilen(stapel: string | undefined): StapelZeile[] {
  if (!stapel) return [];
  const zeilen: StapelZeile[] = [];
  for (const roh of stapel.split("\n")) {
    const zeile = roh.trim();
    const chrome = /^at (?:(.+?) \()?(.+?):(\d+):(\d+)\)?$/.exec(zeile);
    const safari = /^(.*?)@(.+?):(\d+):(\d+)$/.exec(zeile);
    const treffer = chrome ?? safari;
    if (!treffer) continue;
    zeilen.push({
      ...(treffer[1] ? { function: treffer[1] } : {}),
      filename: ohneKennungen(treffer[2]!),
      lineno: Number(treffer[3]),
      colno: Number(treffer[4]),
    });
  }
  // Sentry erwartet die älteste Stelle zuerst.
  return zeilen.reverse().slice(-50);
}

const UUID = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi;

/** Entfernt Kennungen und Suchteile aus Adressen – sie könnten auf Kunden zeigen. */
export function ohneKennungen(text: string): string {
  return text.replace(/\?.*$/, "").replace(UUID, ":id");
}

/**
 * Eine Stelle im Code, wie V8 (Chrome, Node) sie schreibt: „at name (datei:1:2)“,
 * „at async name (…)“, „at new Name (…)“, „at name [as anders] (…)“, „at datei:1:2“.
 * Ohne Leerzeichen im Namen und ohne „@“ – so sieht keine Zeile mit einer
 * Adresse oder einer E-Mail wie eine Stelle aus.
 */
const STELLE_V8 = /^\s*at (?:async )?(?:(?:new )?[^\s()[\]@]+(?: \[as [^\s()[\]@]+\])? \()?[^\s()@]+:\d+:\d+\)?\s*$/;
/** … und wie Safari und Firefox: „name@datei:1:2“. */
const STELLE_SAFARI = /^[\w$.<>]*@[^\s@]+:\d+:\d+$/;

/**
 * Vom Stapel eines Fehlers nur die Stellen im Code (Gutachten vom 3.11.2026,
 * Befund 4; zuerst in den Ablauf-Karten des CRM). Ein Stapel aus V8 beginnt
 * mit der Meldung des Fehlers, und die kann über mehrere Zeilen gehen – bei
 * einem Fehler der Datenbank samt SQL und Werten („Failed query: … params: …“).
 * Mit `fehler` fällt diese Kopfzeile zuerst weg, so wie V8 sie schreibt
 * (`${name}: ${message}`), auch wenn eine ihrer Zeilen wie eine Stelle
 * aussieht; danach jede Zeile, die keine Stelle im Code ist.
 */
export function nurStellenImCode(stapel: string | undefined, fehler?: { name: string; message: string }): string {
  if (!stapel) return "";
  const kopf = fehler ? (fehler.message ? `${fehler.name}: ${fehler.message}` : fehler.name) : null;
  const rest = kopf && stapel.startsWith(kopf) ? stapel.slice(kopf.length) : stapel;
  return rest
    .split("\n")
    .filter((zeile) => STELLE_V8.test(zeile) || STELLE_SAFARI.test(zeile))
    .join("\n");
}

export interface Dsn {
  schluessel: string;
  projekt: string;
  basis: string;
}

/** Zerlegt die DSN aus GlitchTip: https://<schlüssel>@fehler.…/<projekt>. */
export function dsnLesen(dsn: string | undefined): Dsn | null {
  if (!dsn) return null;
  try {
    const url = new URL(dsn);
    const projekt = url.pathname.replace(/^\/+|\/+$/g, "");
    if (!url.username || !/^\d+$/.test(projekt)) return null;
    return { schluessel: url.username, projekt, basis: `${url.protocol}//${url.host}` };
  } catch {
    return null;
  }
}

export function sentryEreignis(m: FehlerMeldung, umgebung: string, jetzt = new Date()): Record<string, unknown> {
  const kennung = globalThis.crypto.randomUUID().replace(/-/g, "");
  return {
    event_id: kennung,
    timestamp: jetzt.toISOString(),
    platform: m.app === "server" ? "node" : "javascript",
    level: "error",
    environment: umgebung,
    tags: { app: m.app },
    ...(m.ort ? { transaction: ohneKennungen(m.ort) } : {}),
    exception: {
      values: [
        {
          type: m.typ.slice(0, 200),
          value: ohneKennungen(m.meldung).slice(0, 2000),
          // Nur Stellen im Code – auch von Geräten mit altem Stand, die den ganzen Stapel schicken (Befund 4).
          stacktrace: { frames: stapelZeilen(nurStellenImCode(m.stapel, { name: m.typ, message: m.meldung })) },
        },
      ],
    },
  };
}
